import { Button } from '@folio/components/ui/button/button';
import { Card, CardContent } from '@folio/components/ui/card/card';
import { formatDate } from '@folio/lib';
import { useQuery } from '@tanstack/react-query';
import {
  type AnonymousSessionPolicy,
  customerControllerGetCustomerQueryOptions,
  policyControllerGetPolicyQueryOptions,
  viewSpecificControllerListPolicyAssetsQueryOptions,
} from 'korint-sdk';
import { useKorintProductConfig } from 'korint-sdk/react';
import { ChevronRightIcon, Loader2Icon, PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Center, Stack } from '@folio/components/ui/flex';
import { Text, Title } from '@folio/components/ui/text';

function AnonymousPolicyCard({
  policy,
  index,
  authHeaders,
  onSelect,
}: {
  policy: AnonymousSessionPolicy & { policyId: string };
  index: number;
  authHeaders: Record<string, string>;
  onSelect: (policy: AnonymousSessionPolicy) => void;
}) {
  const { t } = useTranslation();
  const { productsConfig } = useKorintProductConfig();

  const { data: policyData } = useQuery(
    policyControllerGetPolicyQueryOptions(policy.policyId, undefined, { headers: authHeaders }),
  );
  const { data: assetsResponse } = useQuery(
    viewSpecificControllerListPolicyAssetsQueryOptions(
      policy.policyId,
      policy.branchId ? { branchId: policy.branchId } : undefined,
      { headers: authHeaders },
    ),
  );
  const { data: customerData } = useQuery(
    customerControllerGetCustomerQueryOptions(policy.customerId, undefined, {
      headers: authHeaders,
    }),
  );

  const product = policyData?.productId ? productsConfig[policyData.productId] : undefined;
  const displayName =
    product?.redirectDisplayName && policyData && customerData
      ? product.redirectDisplayName({
          assets: assetsResponse?.data ?? [],
          policy: policyData,
          customer: customerData,
        })
      : undefined;

  const title = displayName || t('anonymousSelection.quoteLabel', { number: index + 1 });
  const quotedOn = policyData?.createdAt
    ? t('anonymousSelection.quotedOn', { date: formatDate(new Date(policyData.createdAt)) })
    : undefined;

  return (
    <button type="button" className="w-full text-left" onClick={() => onSelect(policy)}>
      <Card className="hover:border-primary transition-colors">
        <CardContent className="flex items-center justify-between gap-4 p-4">
          <span className="flex flex-col">
            <Text inline size="sm" weight="medium">
              {title}
            </Text>
            {quotedOn && (
              <Text inline size="xs" tone="muted">
                {quotedOn}
              </Text>
            )}
          </span>
          <ChevronRightIcon className="w-5 h-5 text-muted-foreground" />
        </CardContent>
      </Card>
    </button>
  );
}

export function AnonymousPolicySelection({
  policies,
  authHeaders,
  onSelect,
  onCreateNew,
}: {
  policies: AnonymousSessionPolicy[];
  authHeaders: Record<string, string>;
  onSelect: (policy: AnonymousSessionPolicy) => void;
  onCreateNew: () => void;
}) {
  const { t } = useTranslation();
  const resumable = policies.filter(
    (policy): policy is AnonymousSessionPolicy & { policyId: string } => !!policy.policyId,
  );

  if (resumable.length === 0) {
    return (
      <Center className="py-32">
        <Loader2Icon className="w-8 h-8 animate-spin" />
      </Center>
    );
  }

  return (
    <Stack className="mx-auto max-w-md py-16 px-4">
      <Title order={1} size="lg">
        {t('anonymousSelection.title')}
      </Title>
      {resumable.map((policy, index) => (
        <AnonymousPolicyCard
          key={policy.policyId}
          policy={policy}
          index={index}
          authHeaders={authHeaders}
          onSelect={onSelect}
        />
      ))}
      <Button variant="outline" className="w-full" onClick={onCreateNew}>
        <PlusIcon className="w-4 h-4 mr-2" />
        {t('anonymousSelection.newQuote')}
      </Button>
    </Stack>
  );
}
